import {getDoRequest} from '@/common/reactHandlers';

import AlertModal from '@/components/AlertModal';
import Layout from '@/components/Layout';
import Loading from '@/components/Loading';
import LoginForm from '@/components/login/LoginForm';

import {Mode} from '@/components/Taskbar';
import {INITIAL_MAPSTYLE} from '@/config';
import authService from '@/domain/services/authService';
import createImageSearchRequest, {DateRange} from '@/domain/useCases/createImageSearchRequest';
import {getErrorTranslationKeys} from '@/domain/useCases/exceptionHandling';
import MainContent from '@/views/Map/MainContent';
import {Polygon} from 'geojson';
import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ProviderCredentials} from '@/domain/entities/ProviderCredentials';
import updateProviderCredentials from '@/domain/useCases/updateProviderCredentials';

const Index = () => {
  const {t} = useTranslation();

  const [mode, setMode] = useState<Mode>();
  const [hasPendingRequest, setPendingRequest] = useState(false);
  const [requestSucceeded, setRequestSucceeded] = useState(false);
  const [requestErrorMessageKey, setRequestErrorMessageKey] = useState<Array<string> | undefined>();
  const [isAuthenticated, setAuthenticated] = useState<boolean>();
  const [areaOfInterest, setAreaOfInterest] = useState<Polygon>();
  const [dateRange, setDateRange] = useState<Partial<DateRange>>({});
  // numérico para forzar remount de GeometryEditor
  const [isGeometryEditorOpen, setIsGeometryEditorOpen] = useState<number>(0);
  const [isDrawingStarted, setIsDrawingStarted] = useState<boolean>(false);
  const [providerCredentials, setProviderCredentials] = useState<Partial<ProviderCredentials>>();

  const doRequest = getDoRequest(setPendingRequest, setRequestSucceeded, setRequestErrorMessageKey);
  const resetStates = () => {
    setRequestSucceeded(false);
    setRequestSucceeded(false);
    setPendingRequest(false);
    setAreaOfInterest(undefined);
    setIsGeometryEditorOpen(0);
    setDateRange({});
    setRequestErrorMessageKey(undefined);
    setIsDrawingStarted(false);
  };

  const handleRequestImages = async () => {
    setMode(undefined);
    doRequest(() => createImageSearchRequest(dateRange, areaOfInterest), {
      catchDo: (error) => setRequestErrorMessageKey(getErrorTranslationKeys(error))
    });
  };

  const handleProviderCredentialsChange = (field: keyof ProviderCredentials, value: string) =>
    setProviderCredentials(prev => ({...prev, [field]: value}));

  const handleAirbusCredentials = () => {
    if(!providerCredentials?.airbusApiKey) return;
    doRequest(() => updateProviderCredentials({airbusApiKey: providerCredentials.airbusApiKey}), {
      thenDo: () => setProviderCredentials(undefined)
    });
  };

  const handleCleosCredentials = () => {
    if(!providerCredentials?.cleosPassword || !providerCredentials?.cleosUsername) return;
    doRequest(() => updateProviderCredentials({cleosPassword: providerCredentials.cleosPassword, cleosUsername: providerCredentials.cleosUsername}), {
      thenDo: () => setProviderCredentials(undefined)
    });
  };

  const doLogout = () => {
    authService.logout();
    setAuthenticated(false);
  };
  const doLogin = (username: string, password: string) => {
    doRequest(() => authService.login(username, password), {
      thenDo: () => {
        setAuthenticated(true);
        resetStates();
      }
    });
  };

  const isRequestImagesEnabled = useMemo(
    () => [areaOfInterest, dateRange.start, dateRange.end].every(value => value !== undefined),
    [dateRange, areaOfInterest]);

  const handleDrawStart = () => {
    if (!isDrawingStarted && !areaOfInterest) {
      setIsGeometryEditorOpen((cur) => cur + 1);
      setIsDrawingStarted(true);
      return;
    }
    setAreaOfInterest(undefined);
    setIsDrawingStarted(false);
    setIsGeometryEditorOpen(0);
  };

  useEffect(() => {
    if (mode !== Mode.SEARCH_IMAGE) {
      resetStates();
    }
  }, [mode]);

  useEffect(() => {
    authService.isAuthenticated()
      .then(setAuthenticated)
      .catch(() => setAuthenticated(false));
  }, []);

  const hasDrawnGeometry = isDrawingStarted || areaOfInterest !== undefined;

  if (isAuthenticated === undefined) {
    return <Loading/>;
  } else if (!isAuthenticated) {
    return <LoginForm onLogin={doLogin} isLoading={hasPendingRequest} errorMessageKeys={requestErrorMessageKey}/>;
  } else {
    const mainContent =
      <MainContent
        mapStyle={INITIAL_MAPSTYLE}
        isGeometryEditorOpen={isGeometryEditorOpen}
        onPolygonChange={setAreaOfInterest}
      />;
    return <>
      {hasPendingRequest && <Loading/>}
      <Layout
        mainContent={mainContent}
        mode={mode}
        dateRange={dateRange}
        isLoadingSettings={hasPendingRequest}
        isRequestImagesEnabled={isRequestImagesEnabled}
        hasDrawnGeometry={hasDrawnGeometry}
        providerCredentials={providerCredentials}
        onModeChanged={setMode}
        onDateRangeChange={setDateRange}
        onRequestImages={handleRequestImages}
        onLogout={doLogout}
        onDrawStart={handleDrawStart}
        onAirbusSave={handleAirbusCredentials}
        onCleosSave={handleCleosCredentials}
        onProvidersCredentialsChange={handleProviderCredentialsChange}
      />
      <AlertModal isOpen={requestSucceeded} severity="success"
        messages={[t('request.success')]}
        onClose={() => setRequestSucceeded(false)}/>
      {requestErrorMessageKey !== undefined && <AlertModal isOpen={true} severity="error"
        messages={requestErrorMessageKey.map(key => t(key))}
        onClose={() => setRequestErrorMessageKey(undefined)}/>}
    </>;
  }
};

export default Index;
